import type { QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { ErrorState } from '@/components/QueryState'
import { SkeletonText } from '@/components/ui/skeleton'
import type { CrumbComponent, CrumbTrail } from '@/shell/Breadcrumbs'
import { routeTree } from './routeTree.gen'

export interface RouterContext {
  queryClient: QueryClient
}

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Let TanStack Query own caching; loaders only prime it.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    // Pages scroll inside the shell's <main>, not the window.
    scrollToTopSelectors: ['#main'],
    defaultPendingMs: 250,
    defaultPendingComponent: () => <SkeletonText lines={4} className="max-w-xl" />,
    defaultErrorComponent: ({ error, reset }) => <ErrorState error={error} onRetry={reset} />,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
  interface StaticDataRouteOption {
    /** Breadcrumb label, or a component that renders one from the route params. */
    crumb?: string | CrumbComponent
    /**
     * Breadcrumbs to show before this route's own crumb, in place of its
     * ancestors' crumbs, given the route's search params. Return undefined to
     * keep the ancestors. Lets a shared page keep the trail it was opened from.
     */
    crumbTrail?: CrumbTrail
    /** Remove page padding and scrolling so the page can lay out a full-height split view. */
    fullBleed?: boolean
  }
}
